'use client';

import { useCallback, useMemo } from 'react';
import { PrivilegeEnum, type MaterialDto } from '@/api/ApiClient';
import { useBackend } from '@/hooks/useBackend';
import { usePrivileges } from '@/hooks/usePrivileges';
import { useSnackbar } from '@/hooks/useSnackbar';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { getInlineMaterialEntityConfig } from '@/lib/materials/materialEntityConfig';
import { setCachedOption } from '@/lib/options/optionsCache';
import { log } from '@/lib/utils/logger';

export type CreateMaterialResult = {
  material: MaterialDto;
  freshMaterials: MaterialDto[];
};

export type CreateMaterialOptions = {
  /** When true, marks the new row as substrate / bulk-precursor catalog (Material.IsSubstrate). */
  forSubstrate?: boolean;
};

/**
 * JWT-backed material create helpers for inline dialogs.
 * Matches POST /materials: global Material create + optional ConfidentialMaterial for isConfidential.
 */
export function useCreateMaterial() {
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { isAuthorized } = usePrivileges();

  const canCreateMaterial = isAuthorized(PrivilegeEnum.Material, 'create');
  const canSetConfidential = isAuthorized(PrivilegeEnum.ConfidentialMaterial, 'create');

  const inlineMaterialEntityConfig = useMemo(() => getInlineMaterialEntityConfig(), []);

  const createMaterial = useCallback(
    async (
      materialData: Record<string, unknown>,
      options?: CreateMaterialOptions
    ): Promise<CreateMaterialResult | null> => {
      try {
        const forSubstrate = options?.forSubstrate ?? false;
        const payload: MaterialDto = {
          ...(materialData as MaterialDto),
          isSubstrate: forSubstrate,
          isConfidential: canSetConfidential ? Boolean(materialData.isConfidential) : false,
        };
        const savedMaterial = await backend.material.create(payload);
        const freshMaterials = await backend.material.list('All', false);
        setCachedOption('materials', freshMaterials);
        return { material: savedMaterial, freshMaterials };
      } catch (error) {
        log.error('[useCreateMaterial] Error creating material:', error);
        showSnackbar(getProblemDetail(error) ?? 'Failed to create material.', 'error');
        return null;
      }
    },
    [backend.material, canSetConfidential, showSnackbar]
  );

  return {
    canCreateMaterial,
    canSetConfidential,
    inlineMaterialEntityConfig,
    createMaterial,
  };
}
